import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpWrapperService } from './http-wrapper.service';
import {
  AuditEntityType,
  AuditHistoryResponse,
  AuditQueryOptions,
  AuditStateResponse,
  AuditViewRequest,
  AuditViewResponse
} from '../models/audit.model';

/**
 * Audit Service
 * 
 * Handles audit trail API integration per ADR-0012.
 * - View tracking for entity detail views
 * - Admin query endpoints for audit history
 */
@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private readonly auditUrl = '/api/audit';

  constructor(private http: HttpWrapperService) {}

  /**
   * Track entity view (fire-and-forget)
   * Called when user opens entity detail view.
   * Backend handles 5-minute deduplication.
   */
  trackView(entityType: AuditEntityType, entityId: string, entityTitle?: string): void {
    const body: AuditViewRequest = {
      entityType,
      entityId,
      entityTitle
    };

    // Fire-and-forget: no error handling, no blocking
    this.http.post<AuditViewResponse>(`${this.auditUrl}/view`, body, { 
      showErrors: false 
    }).subscribe({
      // Silently ignore errors - view tracking should never impact UX
      error: () => {}
    });
  }

  /**
   * Get entity audit history (admin only)
   */
  getEntityHistory(
    entityType: string, 
    entityId: string, 
    options?: AuditQueryOptions
  ): Observable<AuditHistoryResponse> {
    const params = this.buildParams(options);
    return this.http.get<AuditHistoryResponse>(
      `${this.auditUrl}/entity/${entityType}/${entityId}`,
      { params }
    );
  }

  /**
   * Reconstruct entity state at a specific timestamp (admin only)
   */
  getEntityStateAt(
    entityType: string, 
    entityId: string, 
    timestamp: Date
  ): Observable<AuditStateResponse> {
    const params = new HttpParams().set('at', timestamp.toISOString());
    return this.http.get<AuditStateResponse>(
      `${this.auditUrl}/entity/${entityType}/${entityId}/state`,
      { params }
    );
  }

  /**
   * Get user activity history (admin only)
   */
  getUserActivity(
    userId: string, 
    options?: AuditQueryOptions
  ): Observable<AuditHistoryResponse> {
    const params = this.buildParams(options);
    return this.http.get<AuditHistoryResponse>(
      `${this.auditUrl}/user/${userId}`,
      { params }
    );
  }

  /**
   * Get recent activity across all entities (admin only)
   */
  getRecentActivity(options?: AuditQueryOptions): Observable<AuditHistoryResponse> {
    const params = this.buildParams(options);
    return this.http.get<AuditHistoryResponse>(
      `${this.auditUrl}/recent`,
      { params }
    );
  }

  /**
   * Build HttpParams from query options
   */
  private buildParams(options?: AuditQueryOptions): HttpParams {
    let params = new HttpParams();

    if (!options) {
      return params;
    }

    if (options.page !== undefined) {
      params = params.set('page', options.page.toString());
    }
    if (options.limit !== undefined) {
      params = params.set('limit', options.limit.toString());
    }
    if (options.from) {
      params = params.set('from', options.from);
    }
    if (options.to) {
      params = params.set('to', options.to);
    }
    if (options.action) {
      params = params.set('action', options.action);
    }
    if (options.source) {
      params = params.set('source', options.source);
    }
    if (options.entityType) {
      params = params.set('entity_type', options.entityType);
    }
    if (options.includeSystem !== undefined) {
      params = params.set('include_system', options.includeSystem.toString());
    }

    return params;
  }
}
